import { useId } from 'react';
import { TOKENS } from '../theme';

/**
 * Turn points into a smooth SVG path. Catmull-Rom converted to cubic Bezier,
 * with control points clamped to the plot so the curve never dips below a
 * zero it does not have.
 */
export function smoothPath(points: Array<[number, number]>, minY: number, maxY: number): string {
  const first = points[0];
  if (!first) return '';
  const clamp = (y: number) => Math.min(maxY, Math.max(minY, y));
  let d = `M${first[0]},${first[1]}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i] ?? first;
    const p2 = points[i + 1] ?? p1;
    const p0 = points[i - 1] ?? p1;
    const p3 = points[i + 2] ?? p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = clamp(p1[1] + (p2[1] - p0[1]) / 6);
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = clamp(p2[1] - (p3[1] - p1[1]) / 6);
    d += ` C${c1x},${c1y} ${c2x},${c2y} ${p2[0]},${p2[1]}`;
  }
  return d;
}

/**
 * A small trend line with a soft fill, no axes. Shape only: the tile it sits
 * in carries the number, so this answers "rising or falling?" and nothing more.
 */
export function Sparkline({
  values,
  tone = 'ink',
  width = 120,
  height = 32,
}: {
  values: number[];
  tone?: 'ink' | 'positive';
  width?: number;
  height?: number;
}) {
  // useId gives ':r1:'; colons are not safe inside url(#...).
  const gradientId = `spark${useId().replace(/:/g, '')}`;
  if (values.length < 2) return null;

  const color = tone === 'positive' ? TOKENS.green : TOKENS.text;
  const pad = 2;
  const max = Math.max(...values);
  const min = Math.min(0, ...values);
  const span = max - min || 1;
  const step = (width - pad * 2) / (values.length - 1);
  const points = values.map((v, i): [number, number] => [
    pad + i * step,
    pad + (height - pad * 2) * (1 - (v - min) / span),
  ]);
  const line = smoothPath(points, pad, height - pad);
  const lastX = points[points.length - 1]?.[0] ?? width - pad;
  const area = `${line} L${lastX},${height} L${pad},${height} Z`;

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden
      style={{ display: 'block', overflow: 'visible' }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={tone === 'positive' ? 0.22 : 0.12} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradientId})`} stroke="none" />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
